import { FiMic, FiEdit3, FiDatabase, FiChevronDown, FiChevronRight, FiClock, FiSend, FiShare2, FiSettings } from 'react-icons/fi'

// Ícone Meta customizado para usar currentColor (respeita o tema)
const MetaIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 32 32"
    fill="currentColor"
    className={className}
  >
    <path d="M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11"/>
  </svg>
)

// Ícone de IA customizado (ai_6.svg - estrela/brilho) - usa currentColor para respeitar o tema
const AIAgentIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 1080 1080"
    fill="currentColor"
    className={className}
  >
    <path d="M0 0 C17.32 10.35 22.3 38.27 28.19 56 C43.16 100.59 53.19 124.81 137.46 258.99 C234.51 344.23 368.73 396.8 426.75 425.19 C429.09 437.69 426.13 443.38 409 455.85 C361.52 475.48 297.31 499.23 255.76 519.13 C147.88 599.25 87.23 669.21 34.04 795.49 C14.94 849.69 10.85 859.25 -0.63 869 C-25.39 865.84 -41.29 832.75 -56.54 791.65 C-86.69 715.73 -151.25 619.5 -240.16 536.07 C-356.56 484.44 -426 459.63 -444.81 449.6 C-448.77 434.62 -432.64 415.05 -408.25 405.5 C-303.6 364.86 -219.25 313.5 -151.27 251.07 C-92.87 164.85 -48.99 59.56 -19.13 -0.25 C-5.99 -2.65 0 0 0 0 Z" transform="translate(477.25,178.5)"/>
    <path d="M0 0 C5.87 1.5 9.02 6.76 12.14 11.65 C40.68 81.19 73.94 130.75 118.94 167.75 C178.12 193.54 218.94 215.75 219.94 221.75 C210.47 229.25 175.66 245.02 162.76 250.61 C89.16 293.71 49.63 340.19 25.14 396.89 C2.94 437.75 -2.69 437.82 -8.06 437.75 C-17.06 408.49 -51.73 327.75 -137.06 259.75 C-212.01 231.28 -217.68 223.22 -217.19 214.69 C-203.03 204.09 -166.14 189.08 -92.06 147.75 C-24.64 43.58 -9.76 8.56 0 0 Z" transform="translate(852.06,9.25)"/>
  </svg>
)

interface CollapsiblePanelProps {
  id: string
  title: string
  icon: React.ReactNode
  isExpanded: boolean
  onToggle: (id: string) => void
  children: React.ReactNode
  className?: string
  subtitle?: string
  headerAction?: React.ReactNode
}

export function CollapsiblePanel({
  id,
  title,
  icon,
  isExpanded,
  onToggle,
  children,
  className = '',
  subtitle,
  headerAction
}: CollapsiblePanelProps) {

  return (
    <div className={`bg-card border border-border rounded-lg shadow-lg flex flex-col ${isExpanded ? 'flex-1 min-h-0' : ''} ${className}`}>
      {/* Header - Altura igual ao header das abas */}
      <div className="flex items-center justify-between px-4 py-3 hover:bg-secondary/50 transition-colors rounded-t-lg flex-shrink-0">
        <button
          onClick={() => onToggle(id)}
          className="flex items-center gap-3 flex-1"
        >
          <div className="text-primary">
            {icon}
          </div>
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-card-foreground">
              {title}
            </h3>
            {subtitle && (
              <span className="text-xs text-muted-foreground font-mono">
                {subtitle}
              </span>
            )}
          </div>
        </button>

        <div className="flex items-center gap-2">
          {/* Ação customizada no header (só aparece quando expandido) */}
          {isExpanded && headerAction}

          <button
            onClick={() => onToggle(id)}
            className="text-muted-foreground transition-transform duration-200 p-1 hover:text-foreground"
          >
            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className={`transition-all duration-300 ease-in-out ${
        isExpanded ? 'flex-1 opacity-100 overflow-hidden min-h-0' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        <div className="h-full overflow-hidden">
          <div 
            className="h-full overflow-y-auto scrollbar-hidden" 
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

interface DraggablePanelsContainerProps {
  children: React.ReactNode
  className?: string
}

export function DraggablePanelsContainer({ children, className = '' }: DraggablePanelsContainerProps) {
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {children}
    </div>
  )
}

export const PANEL_ICONS = {
  transcription: <FiMic className="w-5 h-5" />,
  prompt: <FiEdit3 className="w-5 h-5" />,
  rag: <FiDatabase className="w-5 h-5" />,
  devices: <MetaIcon className="w-5 h-5" />,
  agents: <AIAgentIcon className="w-5 h-5" />,
  schedule: <FiClock className="w-5 h-5" />,
  blast: <FiSend className="w-5 h-5" />,
  campaigns: <FiShare2 className="w-5 h-5" />,
  settings: <FiSettings className="w-5 h-5" />
} as const